import { Stack, usePathname } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import { useAuth } from 'shared/providers/auth';
import theme from 'shared/styles/theme';

const Success = () => {
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.success',
  });
  const { handleLogout } = useAuth();
  const { initiateWhereToGo, isLoading: isWhereToGoFetching } = useWhereToGo();

  const onNextButtonAction = () => {
    handleDoneButtonAnalytics();

    initiateWhereToGo();
  };

  const handleDoneButtonAnalytics = usePressableAnalytics({
    analyticsId: 'doneButtonClicked',
    customAttributes: {
      cta: 'Done',
    },
  });

  useStepStartedFocusAnalytics({
    analyticsId: 'resetPasswordCompleted',
  });

  const pathName = usePathname();

  useEffect(() => {
    const popstateListener = async () => {
      if (pathName === '/reset-password/success') {
        await handleLogout({ shouldNavigateToLogin: true });
      }
    };
    if (isWeb) {
      addEventListener('popstate', popstateListener);
    }
  }, [handleLogout, pathName]);

  useBackHandler(async () => {
    await handleLogout({ shouldNavigateToLogin: true });
  });

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />

      <SuccessScreen
        visual="LOCK_A"
        titleText={translatedText('title')}
        content={
          <BodyBaseText style={styles.textAlignment} numberOfLines={0}>
            {translatedText('description')}
          </BodyBaseText>
        }
        primaryButton={{
          label: translatedText('buttons.done'),
          onPress: () => {
            onNextButtonAction();
          },
          isLoading: isWhereToGoFetching,
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    minWidth: `100%`,
    paddingVertical: theme.spacing.m,
  },

  textAlignment: {
    textAlign: 'center',
  },
});

export default Success;
